// ═══════════════════════════════════════════════════════════════════════════
// WRITTN - Storage Service
// ═══════════════════════════════════════════════════════════════════════════
// Handles all local storage operations for favorites, session state, etc.
// Integrates with cloudSync for real-time Firestore backup
// ═══════════════════════════════════════════════════════════════════════════

import { SETTINGS } from '../config/settings.js';

const PREFIX = SETTINGS.storagePrefix;

// Cloud sync imports (lazy loaded to avoid circular deps)
let cloudSync = null;
async function getCloudSync() {
    if (!cloudSync) {
        const module = await import('./cloudSync.js');
        cloudSync = module;
    }
    return cloudSync;
}

// ─────────────────────────────────────────────────────────────────────────
// CORE STORAGE OPERATIONS
// ─────────────────────────────────────────────────────────────────────────

function getItem(key, defaultValue = null) {
    try {
        const item = localStorage.getItem(PREFIX + key);
        return item ? JSON.parse(item) : defaultValue;
    } catch (e) {
        console.warn('Storage read error:', e);
        return defaultValue;
    }
}

function setItem(key, value) {
    try {
        localStorage.setItem(PREFIX + key, JSON.stringify(value));
        return true;
    } catch (e) {
        console.warn('Storage write error:', e);
        return false;
    }
}

function removeItem(key) {
    try {
        localStorage.removeItem(PREFIX + key);
        return true;
    } catch (e) {
        return false;
    }
}

// ─────────────────────────────────────────────────────────────────────────
// FAVORITES (SAVED VERSES)
// ─────────────────────────────────────────────────────────────────────────

export function getFavorites() {
    return getItem('favorites', []);
}

export function saveFavorite(verse) {
    const favorites = getFavorites();

    // Check if already saved
    if (favorites.some(v => v.reference === verse.reference)) {
        return false;
    }

    // Add with timestamp
    favorites.push({
        ...verse,
        savedAt: new Date().toISOString(),
        note: null,
    });

    // Trim to max
    while (favorites.length > SETTINGS.maxSavedVerses) {
        favorites.shift();
    }

    setItem('favorites', favorites);

    // Trigger cloud sync
    getCloudSync().then(sync => sync.syncFavorites(favorites));

    return true;
}

export function removeFavorite(reference) {
    const favorites = getFavorites();
    const filtered = favorites.filter(v => v.reference !== reference);
    setItem('favorites', filtered);

    // Trigger cloud sync
    getCloudSync().then(sync => sync.syncFavorites(filtered));

    return true;
}

export function isFavorite(reference) {
    const favorites = getFavorites();
    return favorites.some(v => v.reference === reference);
}

export function updateFavoriteNote(reference, note) {
    const favorites = getFavorites();
    const index = favorites.findIndex(v => v.reference === reference);
    if (index !== -1) {
        favorites[index].note = note;
        setItem('favorites', favorites);
    }
}

// Sync favorites from cloud (for offline backup)
export function syncFromCloud(cloudFavorites) {
    if (!cloudFavorites || cloudFavorites.length === 0) return;

    // Cloud is source of truth - replace local with cloud data
    const formatted = cloudFavorites.map(f => ({
        reference: f.reference,
        text: f.text,
        book: f.book || null,
        chapter: f.chapter || null,
        verseNum: f.verseNum || null,
        category: f.category || null,
        theme: f.theme || null,
        savedAt: f.savedAt?.toDate?.()?.toISOString() || f.savedAt || new Date().toISOString(),
        note: f.note || null,
    }));

    setItem('favorites', formatted);
}

// ─────────────────────────────────────────────────────────────────────────
// SESSION STATE
// ─────────────────────────────────────────────────────────────────────────

export function getSessionState() {
    return getItem('session', {
        seenVerses: [],
        sessionVerseCount: 0,
        totalVerseCount: 0,
        currentSessionFavorites: [],
        sessionStartTime: null,
        lastVerseTime: null,
    });
}

export function updateSessionState(updates) {
    const current = getSessionState();
    setItem('session', { ...current, ...updates });
}

export function markVerseSeen(reference) {
    const state = getSessionState();
    if (!state.seenVerses.includes(reference)) {
        state.seenVerses.push(reference);
    }
    state.sessionVerseCount++;
    state.totalVerseCount++;
    state.lastVerseTime = Date.now();

    if (!state.sessionStartTime) {
        state.sessionStartTime = Date.now();
    }

    setItem('session', state);
}

export function addSessionFavorite(verse) {
    const state = getSessionState();
    if (!state.currentSessionFavorites.some(v => v.reference === verse.reference)) {
        state.currentSessionFavorites.push(verse);
        setItem('session', state);
    }
}

export function resetSession() {
    const state = getSessionState();
    setItem('session', {
        ...state,
        sessionVerseCount: 0,
        currentSessionFavorites: [],
        sessionStartTime: null,
        lastVerseTime: null,
    });
}

export function getSeenVerses() {
    return getSessionState().seenVerses;
}

// ─────────────────────────────────────────────────────────────────────────
// USER PREFERENCES
// ─────────────────────────────────────────────────────────────────────────

export function getPreferences() {
    return getItem('preferences', {
        theme: SETTINGS.defaultTheme,
        hasCompletedOnboarding: false,
        hasSeenFirstSession: false,
        soundEnabled: SETTINGS.enableSounds,
        hapticsEnabled: SETTINGS.enableHaptics,
    });
}

export function updatePreferences(updates) {
    const current = getPreferences();
    setItem('preferences', { ...current, ...updates });
}

export function hasCompletedOnboarding() {
    return getPreferences().hasCompletedOnboarding;
}

export function setOnboardingComplete() {
    updatePreferences({ hasCompletedOnboarding: true });
}

export function hasSeenFirstSession() {
    return getPreferences().hasSeenFirstSession;
}

export function setFirstSessionComplete() {
    updatePreferences({ hasSeenFirstSession: true });
}

export function getTheme() {
    return getPreferences().theme;
}

export function setTheme(theme) {
    updatePreferences({ theme });

    // Trigger cloud sync of settings
    getCloudSync().then(sync => sync.syncSettings({ theme, soakTime: getSoakTime() }));
}

// ─────────────────────────────────────────────────────────────────────────
// DIVINE VERSE TRACKING
// ─────────────────────────────────────────────────────────────────────────

export function getDivineVerseCount() {
    return getItem('divineVerseCount', 0);
}

export function incrementDivineVerseCount() {
    const count = getDivineVerseCount() + 1;
    setItem('divineVerseCount', count);
    return count;
}

export function resetDivineVerseCount() {
    setItem('divineVerseCount', 0);
}

// ─────────────────────────────────────────────────────────────────────────
// SOAK TIME (Verse Dwell Time)
// ─────────────────────────────────────────────────────────────────────────

const DEFAULT_SOAK_TIME = 10; // seconds

export function getSoakTime() {
    return getItem('soakTime', DEFAULT_SOAK_TIME);
}

export function setSoakTime(seconds) {
    setItem('soakTime', seconds);

    // Trigger cloud sync of settings
    getCloudSync().then(sync => sync.syncSettings({ theme: getTheme(), soakTime: seconds }));
}

export function hasSoakTimeOnboarded() {
    return getItem('soakTimeOnboarded', false);
}

export function setSoakTimeOnboarded() {
    setItem('soakTimeOnboarded', true);
}

// ─────────────────────────────────────────────────────────────────────────
// ACTION BAR LAYOUT
// ─────────────────────────────────────────────────────────────────────────

export function getActionBarLayout() {
    return getItem('actionBarLayout', '2-row'); // '1-row' or '2-row'
}

export function setActionBarLayout(layout) {
    setItem('actionBarLayout', layout);
}

// ─────────────────────────────────────────────────────────────────────────
// JOURNAL ENTRIES
// ─────────────────────────────────────────────────────────────────────────

const JOURNAL_KEY = 'writtn_journal';

export function getJournalEntries() {
    return getItem(JOURNAL_KEY, []);
}

export function getJournalEntry(entryId) {
    const entries = getJournalEntries();
    return entries.find(e => e.id === entryId) || null;
}

export function getJournalEntriesForVerse(verseRef) {
    const entries = getJournalEntries();
    return entries.filter(e => e.verseRef === verseRef);
}

export function hasJournalEntry(verseRef) {
    const entries = getJournalEntries();
    return entries.some(e => e.verseRef === verseRef);
}

export function saveJournalEntry(entry) {
    const entries = getJournalEntries();

    // Generate ID if new entry
    if (!entry.id) {
        entry.id = 'journal_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        entry.createdAt = new Date().toISOString();
    }
    entry.updatedAt = new Date().toISOString();

    // Check if updating existing entry
    const existingIndex = entries.findIndex(e => e.id === entry.id);
    if (existingIndex >= 0) {
        entries[existingIndex] = entry;
    } else {
        entries.unshift(entry); // Add to beginning (most recent first)
    }

    setItem(JOURNAL_KEY, entries);

    // Trigger cloud sync
    getCloudSync().then(sync => sync.syncJournals(entries));

    return entry;
}

export function deleteJournalEntry(entryId) {
    const entries = getJournalEntries();
    const filtered = entries.filter(e => e.id !== entryId);
    setItem(JOURNAL_KEY, filtered);

    // Trigger cloud sync
    getCloudSync().then(sync => sync.syncJournals(filtered));
}

export function searchJournalEntries(searchTerm) {
    const entries = getJournalEntries();
    const term = searchTerm.toLowerCase();
    return entries.filter(e =>
        e.content.toLowerCase().includes(term) ||
        e.verseRef.toLowerCase().includes(term) ||
        e.verseText?.toLowerCase().includes(term)
    );
}

// ─────────────────────────────────────────────────────────────────────────
// EXPORT ALL
// ─────────────────────────────────────────────────────────────────────────

export default {
    getFavorites,
    saveFavorite,
    removeFavorite,
    isFavorite,
    updateFavoriteNote,
    getSessionState,
    updateSessionState,
    markVerseSeen,
    addSessionFavorite,
    resetSession,
    getSeenVerses,
    getPreferences,
    updatePreferences,
    hasCompletedOnboarding,
    setOnboardingComplete,
    hasSeenFirstSession,
    setFirstSessionComplete,
    getTheme,
    setTheme,
    getDivineVerseCount,
    incrementDivineVerseCount,
    resetDivineVerseCount,
    getSoakTime,
    setSoakTime,
    hasSoakTimeOnboarded,
    setSoakTimeOnboarded,
    getActionBarLayout,
    setActionBarLayout,
    getJournalEntries,
    getJournalEntry,
    getJournalEntriesForVerse,
    hasJournalEntry,
    saveJournalEntry,
    deleteJournalEntry,
    searchJournalEntries,
};
